// client/src/components/shared/NotificationCenter.tsx

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Bell, X, Check, XCircle, Edit3, Clock, DollarSign, BookOpen, Users, Trophy, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import notificationService, { Notification } from '../../services/notificationService';
import ChallengeRequestModal from '../compete/ChallengeRequestModal';
import EditedChallengeReviewModal from '../compete/EditedChallengeReviewModal';
import { ChallengeRequestData, EditedChallengeData } from '../../types/challenge.types';
import { toast } from 'react-toastify';

interface NotificationCenterProps {
  onChallengeAccepted?: (challengeData: ChallengeRequestData) => void;
  onChallengeEdited?: (challengeData: ChallengeRequestData) => void;
  onChallengeDeclined?: (reason: string) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  onChallengeAccepted,
  onChallengeEdited,
  onChallengeDeclined
}) => {
  const { user: authUser, token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedChallenge, setSelectedChallenge] = useState<{
    notification: Notification;
    data: ChallengeRequestData;
  } | null>(null);
  const [selectedEditedChallenge, setSelectedEditedChallenge] = useState<{
    notification: Notification;
    data: EditedChallengeData;
  } | null>(null);
  const [challengeModalMode, setChallengeModalMode] = useState<'review' | 'edit'>('review');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Only close if clicking outside the notification button AND the modal is not open
      // When modal is open, we rely on the modal's own click handling
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && !isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    // Poll for new notifications with exponential backoff on errors
    let consecutiveErrors = 0;
    let timeoutId: NodeJS.Timeout;
    
    const scheduleNextCheck = (delay: number) => {
      timeoutId = setTimeout(() => {
        if (authUser?.id) {
          checkNewNotificationsWithBackoff();
        }
      }, delay);
    };

    const checkNewNotificationsWithBackoff = async () => {
      if (!authUser?.id || !token) return;
      
      try {
        const response = await notificationService.getUnreadCount();
        if (response.success) {
          const newCount = response.count || 0;
          
          // If the unread count has changed, refresh the notification list
          if (newCount !== unreadCount) {
            setUnreadCount(newCount);
            // If notifications are currently open, refresh them
            if (isOpen) {
              loadNotifications();
            }
          }
          
          // Reset error count on success
          consecutiveErrors = 0;
          // Schedule next check in 10 seconds
          scheduleNextCheck(10000);
        }
      } catch (error) {
        console.error('Error checking new notifications:', error);
        consecutiveErrors++;
        
        // Implement exponential backoff: increase delay on consecutive errors
        const delay = Math.min(10000 * Math.pow(2, Math.min(consecutiveErrors - 1, 3)), 60000);
        console.log(`Scheduling next check in ${delay}ms due to ${consecutiveErrors} consecutive errors`);
        scheduleNextCheck(delay);
      }
    };

    // Start the first check
    if (authUser?.id) {
      checkNewNotificationsWithBackoff();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [authUser?.id, unreadCount, isOpen]);

  const loadNotifications = async () => {
    if (!authUser?.id || !token) return;
    
    setIsRefreshing(true);
    try {
      const response = await notificationService.getNotifications();
      if (response.success) {
        const notifs = response.data || [];
        console.log('Loaded notifications:', notifs);
        // Validate notification data
        const validNotifications = notifs.filter((n: Notification) => {
          if (!n.id) {
            console.warn('Notification missing ID:', n);
            return false;
          }
          return true;
        });
        setNotifications(validNotifications);
        setUnreadCount(validNotifications.filter((n: Notification) => n.status === 'unread').length);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const checkNewNotifications = async () => {
    if (!authUser?.id || !token) return;
    
    try {
      const response = await notificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.count || 0);
      }
    } catch (error) {
      console.error('Error checking new notifications:', error);
    }
  };

  const markAsRead = async (notificationId: string) => {
    if (!token || !notificationId) {
      console.warn('Cannot mark notification as read: missing token or notification ID', { token: !!token, notificationId });
      return;
    }
    
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev: Notification[]) => 
        prev.map((n: Notification) => n.id === notificationId ? { ...n, status: 'read' as const } : n)
      );
      setUnreadCount((prev: number) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleChallengeAction = (notification: Notification, action: 'accept' | 'decline' | 'edit') => {
    // Always start in review mode - the modal will handle switching to edit mode
    setChallengeModalMode('review');
    
    console.log('🔍 DEBUG: handleChallengeAction called with action:', action);
    console.log('🔍 DEBUG: Setting challengeModalMode to:', 'review');
    
    // Parse challenge data from notification
    const challengeData: ChallengeRequestData = {
      topic: notification.data?.area?.topic || '',
      subject: notification.data?.area?.subject || '',
      numberOfQuestions: notification.data?.numberOfQuestions || 10,
      stake: notification.data?.stake || 100,
      startTime: notification.data?.startTime || '',
      duration: notification.data?.duration || 30,
      mode: notification.data?.mode || 'live',
      message: notification.data?.message || '',
      id: notification.data?.challengeId // Map challengeId to id for the modal
    };

    console.log('🔍 DEBUG: Parsed challenge data:', challengeData);
    setSelectedChallenge({ notification, data: challengeData });
    // Don't mark as read immediately - only mark as read when action is completed
  };

  const handleEditedChallengeReview = (notification: Notification) => {
    // Parse edited challenge data from notification
    const editedChallengeData: EditedChallengeData = {
      id: notification.data?.challengeId || '',
      originalData: {
        topic: notification.data?.originalArea?.topic || '',
        subject: notification.data?.originalArea?.subject || '',
        numberOfQuestions: notification.data?.originalNumberOfQuestions || 10,
        stake: notification.data?.originalStake || 100,
        startTime: notification.data?.originalStartTime || '',
        duration: notification.data?.originalDuration || 30,
        mode: notification.data?.originalMode || 'live',
        message: notification.data?.originalMessage || '',
      },
      editedData: {
        topic: notification.data?.area?.topic || '',
        subject: notification.data?.area?.subject || '',
        numberOfQuestions: notification.data?.numberOfQuestions || 10,
        stake: notification.data?.stake || 100,
        startTime: notification.data?.startTime || '',
        duration: notification.data?.duration || 30,
        mode: notification.data?.mode || 'live',
        message: notification.data?.message || '',
      },
      editedBy: notification.data?.opponentName || 'Unknown',
      editedAt: notification.data?.editedAt || new Date().toISOString(),
      status: 'pending_review'
    };

    setSelectedEditedChallenge({ notification, data: editedChallengeData });
    // Don't mark as read immediately - only mark as read when action is completed
  };

  const handleChallengeAccepted = async (data: ChallengeRequestData) => {
    if (!token || !selectedChallenge) return;
    
    try {
      const response = await notificationService.acceptChallengeRequest(
        selectedChallenge.notification.data.challengeId || ''
      );
      
      if (response.success) {
        // Mark notification as read and remove from list
        await markAsRead(selectedChallenge.notification.id);
        
        toast.success('Challenge accepted! 🎉');
        
        // Call the callback to navigate to competition playground
        if (onChallengeAccepted) {
          onChallengeAccepted(data);
        } else {
          console.log('Challenge accepted, but no navigation callback provided');
        }
      }
    } catch (error) {
      console.error('Error accepting challenge:', error);
      toast.error('Failed to accept challenge. Please try again.');
    }
    
    setSelectedChallenge(null);
  };

  const handleChallengeDeclined = async (reason: string) => {
    if (!token || !selectedChallenge) return;
    
    try {
      const response = await notificationService.declineChallengeRequest(
        selectedChallenge.notification.data.challengeId || '',
        reason
      );
      
      if (response.success) {
        // Mark notification as read and remove from list
        await markAsRead(selectedChallenge.notification.id);
        
        toast.success('Challenge declined');
        
        // Call the callback to handle the declined challenge
        if (onChallengeDeclined) {
          onChallengeDeclined(reason);
        }
      }
    } catch (error) {
      console.error('Error declining challenge:', error);
      toast.error('Failed to decline challenge. Please try again.');
    }
    
    setSelectedChallenge(null);
  };

  const handleChallengeEdited = async (data: ChallengeRequestData) => {
    if (!token || !selectedChallenge) return;
    
    try {
      const response = await notificationService.editChallengeRequest(
        selectedChallenge.notification.data.challengeId || '',
        {
          opponentId: selectedChallenge.notification.senderId || '',
          area: {
            subject: data.subject,
            topic: data.topic
          },
          mode: data.mode,
          stake: data.stake,
          numberOfQuestions: data.numberOfQuestions,
          startTime: data.startTime,
          duration: data.duration,
          message: data.message
        }
      );
      
      if (response.success) {
        // Mark notification as read and remove from list
        await markAsRead(selectedChallenge.notification.id);
        
        toast.success('Challenge edited and sent back to sender');
        
        // Call the callback to handle the edited challenge
        if (onChallengeEdited) {
          onChallengeEdited(data);
        }
        
        // Only close the modal when edit is actually completed
        // Keep the notification panel open so user can see other notifications
        setSelectedChallenge(null);
      }
    } catch (error) {
      console.error('Error editing challenge:', error);
      toast.error('Failed to edit challenge. Please try again.');
    }
  };

  const handleEditModeSwitch = (data: ChallengeRequestData) => {
    // This is called when switching to edit mode, not when completing the edit
    // Just switch the modal mode, don't close anything
    setChallengeModalMode('edit');
  };

  const handleEditedChallengeAccepted = async (data: ChallengeRequestData) => {
    if (!token || !selectedEditedChallenge) return;
    
    try {
      const response = await notificationService.acceptEditedChallengeRequest(
        selectedEditedChallenge.data.id
      );
      
      if (response.success) {
        // Mark notification as read and remove from list
        await markAsRead(selectedEditedChallenge.notification.id);
        
        toast.success('Edited challenge accepted! 🎉');
        
        // Call the callback to navigate to competition playground
        if (onChallengeAccepted) {
          onChallengeAccepted(data);
        } else {
          console.log('Edited challenge accepted, but no navigation callback provided');
        }
      }
    } catch (error) {
      console.error('Error accepting edited challenge:', error);
      toast.error('Failed to accept edited challenge. Please try again.');
    }
    
    setSelectedEditedChallenge(null);
  };

  const handleEditedChallengeRejected = async (reason: string) => {
    if (!token || !selectedEditedChallenge) return;
    
    try {
      const response = await notificationService.rejectEditedChallengeRequest(
        selectedEditedChallenge.data.id,
        reason
      );
      
      if (response.success) {
        // Mark notification as read and remove from list
        await markAsRead(selectedEditedChallenge.notification.id);
        
        toast.success('Edited challenge rejected');
      }
    } catch (error) {
      console.error('Error rejecting edited challenge:', error);
      toast.error('Failed to reject edited challenge. Please try again.');
    }
    
    setSelectedEditedChallenge(null);
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'challenge_request':
        return <Trophy size={16} />;
      case 'challenge_accepted':
        return <Check size={16} />;
      case 'challenge_declined':
        return <XCircle size={16} />;
      case 'challenge_edited':
        return <Edit3 size={16} />;
      case 'challenge_started':
        return <Clock size={16} />;
      default:
        return <MessageSquare size={16} />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'challenge_request':
        return '#667eea';
      case 'challenge_accepted':
        return '#10b981';
      case 'challenge_declined':
        return '#ef4444';
      case 'challenge_edited':
        return '#f59e0b';
      case 'challenge_started':
        return '#8b5cf6';
      default:
        return '#6b7280';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <NotificationContainer ref={dropdownRef}>
        <NotificationButton onClick={() => setIsOpen(!isOpen)}>
          <Bell size={20} />
          {unreadCount > 0 && (
            <NotificationBadge>
              {unreadCount > 99 ? '99+' : unreadCount}
            </NotificationBadge>
          )}
        </NotificationButton>
      </NotificationContainer>

      {/* Notification Modal Overlay */}
      {isOpen && (
        <NotificationModalOverlay onClick={(e: React.MouseEvent) => {
          // Only close if clicking directly on the overlay background, not on child elements
          if (e.target === e.currentTarget) {
            console.log('🔍 DEBUG: Clicking on overlay background, closing notification panel');
            setIsOpen(false);
          }
        }}>
          <NotificationModalContent onClick={(e: React.MouseEvent) => {
            // Prevent clicks inside the modal content from bubbling up to the overlay
            e.stopPropagation();
          }}>
            <ModalHeader>
              <ModalTitle>Notifications</ModalTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={loadNotifications}
                  disabled={isRefreshing}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px'
                  }}
                  title="Refresh notifications"
                >
                  {isRefreshing ? '🔄' : '🔄'}
                </button>
                <CloseButton onClick={() => setIsOpen(false)}>
                  <X size={20} />
                </CloseButton>
              </div>
            </ModalHeader>

            <ModalBody>
              {loading ? (
                <LoadingMessage>Loading notifications...</LoadingMessage>
              ) : notifications.length === 0 ? (
                <EmptyMessage>No notifications</EmptyMessage>
              ) : (
                <>
                  {/* Help message for challenge notifications */}
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#f0f9ff', 
                    borderBottom: '1px solid #e0e7ff',
                    fontSize: '12px',
                    color: '#1e40af',
                    textAlign: 'center'
                  }}>
                    💡 Challenge notifications will only be marked as read when you complete an action (Accept, Edit, or Decline)
                  </div>
                  
                  {isRefreshing && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '8px', 
                      background: '#f0f9ff', 
                      borderBottom: '1px solid #e0e7ff',
                      fontSize: '12px',
                      color: '#1e40af'
                    }}>
                      🔄 Refreshing notifications...
                    </div>
                  )}
                  {notifications.map((notification: Notification) => {
                    console.log('🔍 DEBUG: Rendering notification:', notification);
                    console.log('🔍 DEBUG: Notification type:', notification.type);
                    console.log('🔍 DEBUG: Notification data:', notification.data);
                    return (
                  <NotificationItem 
                    key={notification.id} 
                    isUnread={notification.status === 'unread'}
                  >
                    <NotificationIcon color={getNotificationColor(notification.type)}>
                      {getNotificationIcon(notification.type)}
                    </NotificationIcon>
                    
                    <NotificationContent>
                      <NotificationTitle>{notification.title}</NotificationTitle>
                      <NotificationMessage>{notification.message}</NotificationMessage>
                      <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                      
                      {/* Add a subtle "Mark as Read" option for non-action notifications */}
                      {notification.status === 'unread' && !notification.type.includes('challenge') && (
                        <div style={{ marginTop: '8px' }}>
                          <button
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#6b7280',
                              fontSize: '11px',
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            Mark as read
                          </button>
                        </div>
                      )}
                      
                      {notification.type === 'challenge_request' && (
                        <>
                          {console.log('🔍 DEBUG: Rendering challenge_request buttons for notification:', notification.id)}
                          <ChallengeActions>
                          <ActionButton 
                            onClick={(e: React.MouseEvent) => {
                              console.log('🔍 DEBUG: Accept button clicked!');
                              e.stopPropagation();
                              e.preventDefault();
                              handleChallengeAction(notification, 'accept');
                            }}
                            color="#10b981"
                          >
                            Accept
                          </ActionButton>
                          <ActionButton 
                            onClick={(e: React.MouseEvent) => {
                              console.log('🔍 DEBUG: Edit button clicked!');
                              e.stopPropagation();
                              e.preventDefault();
                              handleChallengeAction(notification, 'edit');
                            }}
                            color="#f59e0b"
                          >
                            Edit
                          </ActionButton>
                          <ActionButton 
                            onClick={(e: React.MouseEvent) => {
                              console.log('🔍 DEBUG: Decline button clicked!');
                              e.stopPropagation();
                              e.preventDefault();
                              handleChallengeAction(notification, 'decline');
                            }}
                            color="#ef4444"
                          >
                            Decline
                          </ActionButton>
                        </ChallengeActions>
                        </>
                      )}

                      {notification.type === 'challenge_edited' && (
                        <ChallengeActions>
                          <ActionButton 
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleEditedChallengeReview(notification);
                            }}
                            color="#f59e0b"
                          >
                            Review Edit
                          </ActionButton>
                        </ChallengeActions>
                      )}
                    </NotificationContent>
                  </NotificationItem>
                    );
                  })}
              </>
            )}
            </ModalBody>
          </NotificationModalContent>
        </NotificationModalOverlay>
      )}

      {/* Challenge Request Modal */}
      {selectedChallenge && (
        <>
          {console.log('🔍 DEBUG: Rendering ChallengeRequestModal with mode:', challengeModalMode)}
          {console.log('🔍 DEBUG: selectedChallenge data:', selectedChallenge.data)}
          <ChallengeRequestModal
            isOpen={true}
            onClose={() => {
              setSelectedChallenge(null);
              // Don't close the notification panel when modal closes
              // The user should be able to see the notification panel after closing the modal
            }}
            competitor={{
              id: selectedChallenge.notification.senderId || '',
              name: selectedChallenge.notification.senderName || 'Unknown',
              rating: 0,
              wins: 0,
              totalMatches: 0
            }}
            mode={challengeModalMode}
            initialData={selectedChallenge.data}
            onRequestAccepted={handleChallengeAccepted}
            onRequestDeclined={handleChallengeDeclined}
            onRequestEdited={challengeModalMode === 'review' ? handleEditModeSwitch : handleChallengeEdited}
          />
        </>
      )}

      {/* Edited Challenge Review Modal */}
      {selectedEditedChallenge && (
        <EditedChallengeReviewModal
          isOpen={true}
          onClose={() => setSelectedEditedChallenge(null)}
          challengeData={selectedEditedChallenge.data}
          onChallengeAccepted={handleEditedChallengeAccepted}
          onChallengeRejected={handleEditedChallengeRejected}
        />
      )}
    </>
  );
};

// Styled Components
const NotificationContainer = styled.div`
  position: relative;
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  position: relative;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f1f5f9;
    color: #334155;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  line-height: 1;
`;

// Modal Overlay Components
const NotificationModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const NotificationModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    width: 95%;
    max-height: 85vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  
  @media (max-width: 480px) {
    padding: 16px 20px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(80vh - 80px);
  
  @media (max-width: 480px) {
    max-height: calc(85vh - 70px);
  }
`;

const NotificationItem = styled.div<{ isUnread: boolean }>`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props: { isUnread: boolean }) => props.isUnread ? '#fef3c7' : 'white'};
  position: relative;
  
  &:hover {
    background: ${(props: { isUnread: boolean }) => props.isUnread ? '#fde68a' : '#f9fafb'};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  /* Add a subtle indicator for unread notifications */
  ${(props: { isUnread: boolean }) => props.isUnread && `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: #f59e0b;
      border-radius: 0 2px 2px 0;
    }
  `}
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    gap: 10px;
  }
`;

const NotificationIcon = styled.div<{ color: string }>`
  color: ${(props: { color: string }) => props.color};
  flex-shrink: 0;
  margin-top: 2px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props: { color: string }) => `${props.color}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const NotificationMessage = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const NotificationTime = styled.div`
  color: #9ca3af;
  font-size: 0.75rem;
  margin-bottom: 8px;
`;

const ChallengeActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ color: string }>`
  background: ${(props: { color: string }) => props.color};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const EmptyMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
`;

export default NotificationCenter;
